import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSettings } from '../hooks/useGlobalSettings';

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  senha: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { settings } = useGlobalSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    try {
      await login(data.email, data.senha);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060C1F] text-white overflow-hidden">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/2 h-72 w-72 rounded-full bg-[#F37335]/20 blur-3xl" />
        <div className="absolute -bottom-20 right-1/4 h-80 w-80 rounded-full bg-[#4A9EFF]/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(79,70,229,0.16),_transparent_20%),radial-gradient(circle_at_bottom_right,_rgba(255,105,180,0.08),_transparent_30%)]" />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-6xl rounded-[32px] border border-white/10 bg-slate-950/80 shadow-2xl backdrop-blur-xl">
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="hidden rounded-[32px] bg-slate-900/75 p-10 lg:block">
              <div className="mb-10">
                <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/85 shadow-sm">
                  <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
                  Premium SaaS Auth
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <p className="text-sm uppercase tracking-[0.3em] text-slate-400">LeadNexia</p>
                  <h1 className="text-4xl font-semibold leading-tight tracking-tight text-white">Coordene sua operação comercial com inteligência orientada a resultado.</h1>
                  <p className="max-w-xl text-sm leading-7 text-slate-300">Transforme metas em missões, acompanhe riscos, aprove aprovações e conduza sua operação com mais clareza, velocidade e governança.</p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    { title: 'Inteligência comercial', description: 'Diagnostica contexto, organiza prioridades e orienta a execução.' },
                    { title: 'Coordenação operacional', description: 'CRM, campanhas e canais passam a trabalhar para cumprir a missão.' },
                    { title: 'Governança executiva', description: 'Aprovações, riscos e próximas ações em uma leitura clara.' },
                    { title: 'Resultados contínuos', description: 'Acompanha progresso, ROI e aprendizado ao longo da operação.' },
                  ].map((item) => (
                    <div key={item.title} className="rounded-3xl border border-white/10 bg-slate-950/40 p-5 shadow-lg shadow-slate-950/20 backdrop-blur-sm transition hover:-translate-y-1 hover:border-orange-400/30">
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="mt-2 text-sm text-slate-400">{item.description}</p>
                    </div>
                  ))}
                </div>

                <div className="rounded-[32px] border border-white/10 bg-white/5 p-6 shadow-inner shadow-slate-950/40">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Mockup</p>
                      <p className="text-sm font-semibold text-white">Centro de comando em tempo real</p>
                    </div>
                    <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-300">Novo</span>
                  </div>
                  <div className="space-y-3">
                    <div className="h-4 w-3/4 rounded-full bg-slate-700" />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="h-20 rounded-3xl bg-slate-800" />
                      <div className="h-20 rounded-3xl bg-slate-800" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[32px] border border-white/10 bg-slate-950/95 p-8 shadow-2xl shadow-slate-950/40">
              <div className="mb-8">
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-900/80 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-300 shadow-sm shadow-slate-950/20">
                  <span className="h-2.5 w-2.5 rounded-full bg-orange-400" />
                  Acesso Premium
                </div>
                <h2 className="mt-6 text-3xl font-semibold text-white">Entrar na plataforma</h2>
                <p className="mt-3 text-sm text-slate-400">Faça login e continue acompanhando missões, aprovações e resultados.</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-semibold text-slate-300">E-mail</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-500">
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 6.5h16v11H4z" />
                        <path d="M4 6.5l8 6 8-6" />
                      </svg>
                    </span>
                    <input
                      {...register('email')}
                      type="email"
                      id="email"
                      className="w-full rounded-3xl border border-white/10 bg-slate-900/80 px-14 py-4 text-sm text-white placeholder:text-slate-500 focus:border-orange-400/60 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
                      placeholder="seu@email.com"
                      disabled={isSubmitting}
                    />
                  </div>
                  {errors.email && <p className="text-sm text-orange-300">{errors.email.message}</p>}
                </div>

                <div className="space-y-2">
                  <label htmlFor="senha" className="text-sm font-semibold text-slate-300">Senha</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-500">
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="5" y="11" width="14" height="8" rx="2" />
                        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                      </svg>
                    </span>
                    <input
                      {...register('senha')}
                      type={showPassword ? 'text' : 'password'}
                      id="senha"
                      className="w-full rounded-3xl border border-white/10 bg-slate-900/80 px-14 py-4 pr-16 text-sm text-white placeholder:text-slate-500 focus:border-orange-400/60 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
                      placeholder="••••••••"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </div>
                  {errors.senha && <p className="text-sm text-orange-300">{errors.senha.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-3xl bg-gradient-to-r from-orange-400 via-fuchsia-500 to-sky-500 px-6 py-4 text-sm font-semibold text-slate-950 shadow-xl shadow-orange-500/20 transition duration-200 hover:scale-[1.01] hover:shadow-orange-500/30 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? 'Entrando...' : 'Entrar na plataforma'}
                </button>
              </form>

              <div className="mt-8 flex flex-col gap-3 text-center text-sm text-slate-400 sm:flex-row sm:items-center sm:justify-between">
                <p>Comece gratuitamente</p>
                <Link to="/cadastro" className="font-semibold text-white transition hover:text-orange-300">
                  Criar conta →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
